import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;

          // Update tokens
          localStorage.setItem('access_token', access_token);
          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
          }

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clean up and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          sessionStorage.removeItem('pkce_code_verifier');
          sessionStorage.removeItem('oauth_state');

          // Only redirect if not already on login page (prevent loops)
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, clean up and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        sessionStorage.removeItem('pkce_code_verifier');
        sessionStorage.removeItem('oauth_state');

        // Only redirect if not already on login page (prevent loops)
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (code, code_verifier) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      code,
      code_verifier
    });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Generic API
export const dataAPI = {
  getProtected: async () => {
    const response = await api.get('/api/protected');
    return response.data;
  },

  getUserSettings: async () => {
    const response = await api.get('/api/user/settings');
    return response.data;
  },

  updateUserSettings: async (settings) => {
    const response = await api.post('/api/user/settings', settings);
    return response.data;
  },
};

// Demo API - role-based endpoints
export const demoAPI = {
  getVaterData: async () => {
    const response = await api.get('/demo/vater-only');
    return response.data;
  },

  getMutterData: async () => {
    const response = await api.get('/demo/mutter-only');
    return response.data;
  },

  getKindData: async () => {
    const response = await api.get('/demo/kind-only');
    return response.data;
  },

  getParentsData: async () => {
    const response = await api.get('/demo/parents-only');
    return response.data;
  },

  getFamilyData: async () => {
    const response = await api.get('/demo/family');
    return response.data;
  },
};

export default api;
